import { defaultConfig, type TeamPieceDef } from "./config.js";

type PieceRole = TeamPieceDef["type"];
type FormationSpot = { x: number; depthOffset: number };

function cloneDefaultTeamSetup() {
  return {
    teamA: defaultConfig.teamSetup.teamA.map((p) => ({ type: p.type, start: { ...p.start } })),
    teamB: defaultConfig.teamSetup.teamB.map((p) => ({ type: p.type, start: { ...p.start } })),
    ball: defaultConfig.teamSetup.ball === "chooseByNonStarting"
      ? "chooseByNonStarting" as const
      : { ...defaultConfig.teamSetup.ball },
  };
}

function evenLine(count: number): FormationSpot[] {
  if (count <= 0) return [];
  if (count === 1) return [{ x: 0.5, depthOffset: 0 }];
  const margin = count === 2 ? 0.3 : count === 3 ? 0.2 : 0.1;
  return Array.from({ length: count }, (_, i) => ({
    x: margin + i * (1 - margin * 2) / (count - 1),
    depthOffset: 0,
  }));
}

function formationSpots(role: PieceRole, count: number): FormationSpot[] {
  const line = evenLine(count);
  if (role === "defender") {
    // Fullbacks step forward while central defenders hold the deeper line.
    if (count >= 3) {
      line[0].depthOffset = 1;
      line[line.length - 1].depthOffset = 1;
    }
    return line;
  }

  if (role === "midfielder") {
    if (count === 3) line[1].depthOffset = 1;
    if (count === 4) return [
      { x: 0.5, depthOffset: -1 },
      { x: 0.22, depthOffset: 0 },
      { x: 0.78, depthOffset: 0 },
      { x: 0.5, depthOffset: 1 },
    ];
    if (count === 5) return [
      { x: 0.18, depthOffset: 0 },
      { x: 0.5, depthOffset: 0 },
      { x: 0.82, depthOffset: 0 },
      { x: 0.34, depthOffset: 1 },
      { x: 0.66, depthOffset: 1 },
    ];
    return line;
  }

  if (role === "striker") {
    if (count === 2 || count === 3) line[Math.floor(count / 2)].depthOffset = 1;
    if (count === 4) return [
      { x: 0.28, depthOffset: 0 },
      { x: 0.72, depthOffset: 0 },
      { x: 0.42, depthOffset: 1 },
      { x: 0.58, depthOffset: 1 },
    ];
    if (count === 5) return [
      { x: 0.18, depthOffset: 0 },
      { x: 0.5, depthOffset: 0 },
      { x: 0.82, depthOffset: 0 },
      { x: 0.34, depthOffset: 1 },
      { x: 0.66, depthOffset: 1 },
    ];
  }
  return line;
}

export function generateTeamSetup(
  width: number,
  height: number,
  boxHeight: number,
  numDefenders: number,
  numMidfielders: number,
  numStrikers: number,
) {
  // Preserve the hand-tuned canonical opening for the default game.
  if (
    width === defaultConfig.board.width &&
    height === defaultConfig.board.height &&
    numDefenders === 2 &&
    numMidfielders === 1 &&
    numStrikers === 2
  ) return cloneDefaultTeamSetup();

  const centerX = Math.floor(width / 2);
  const half = Math.floor(height / 2);
  const frontRow = Math.max(2, half - 1);
  const defenderDepth = Math.min(frontRow, Math.max(2, boxHeight - 1));
  const availableDepth = Math.max(0, frontRow - defenderDepth);
  const midfieldDepth = Math.min(frontRow, defenderDepth + Math.max(1, Math.round(availableDepth * 0.4)));
  const strikerDepth = Math.min(frontRow, defenderDepth + Math.max(2, Math.round(availableDepth * 0.8)));
  const occupied = new Set<string>([`${centerX},1`]);
  // Even heights: B's kickoff ball sits on the centre of A's front row once
  // mirrored, so no piece may be generated onto it.
  if (height % 2 === 0) occupied.add(`${centerX},${frontRow}`);

  function reserveSpot(preferredX: number, preferredY: number): { x: number; y: number } {
    const y = Math.max(1, Math.min(frontRow, preferredY));
    const x = Math.max(0, Math.min(width - 1, preferredX));
    const candidates: { x: number; y: number }[] = [{ x, y }];
    for (let distance = 1; distance < width; distance++) {
      candidates.push({ x: x - distance, y }, { x: x + distance, y });
    }
    for (let distance = 1; distance <= frontRow; distance++) {
      candidates.push({ x, y: y - distance }, { x, y: y + distance });
    }
    const spot = candidates.find((candidate) =>
      candidate.x >= 0 && candidate.x < width && candidate.y >= 1 && candidate.y <= frontRow &&
      !occupied.has(`${candidate.x},${candidate.y}`),
    ) ?? { x, y };
    occupied.add(`${spot.x},${spot.y}`);
    return spot;
  }

  function buildLine(role: PieceRole, count: number, baseDepth: number): TeamPieceDef[] {
    return formationSpots(role, count).map((spot) => ({
      type: role,
      start: reserveSpot(Math.round(spot.x * (width - 1)), baseDepth + spot.depthOffset),
    }));
  }

  const teamB: TeamPieceDef[] = [
    { type: "goalkeeper", start: { x: centerX, y: 1 } },
    ...buildLine("defender", numDefenders, defenderDepth),
    ...buildLine("midfielder", numMidfielders, midfieldDepth),
    ...buildLine("striker", numStrikers, strikerDepth),
  ];
  const teamA: TeamPieceDef[] = teamB.map((piece) => ({
    type: piece.type,
    start: { x: piece.start.x, y: height - 1 - piece.start.y },
  }));

  return { teamA, teamB, ball: { x: centerX, y: half } };
}
